import React, { useState, useEffect, useRef } from 'react';
import { ArrowRightIcon } from '@heroicons/react/24/outline';

// Add CSS for line-clamp utility
const lineClampStyle = `
  .line-clamp-2 {
    display: -webkit-box;
    -webkit-line-clamp: 2;
    -webkit-box-orient: vertical;
    overflow: hidden;
  }
  
  .line-clamp-3 {
    display: -webkit-box;
    -webkit-line-clamp: 3;
    -webkit-box-orient: vertical;
    overflow: hidden;
  }
  
  .scrollbar-hide {
    -ms-overflow-style: none;
    scrollbar-width: none;
  }
  
  .scrollbar-hide::-webkit-scrollbar {
    display: none;
  }
`;

// Inject styles if not already present
if (typeof document !== 'undefined' && !document.getElementById('chat-suggestions-styles')) {
  const style = document.createElement('style');
  style.id = 'chat-suggestions-styles';
  style.textContent = lineClampStyle;
  document.head.appendChild(style);
}

const ChatSuggestions = ({ suggestions, onSuggestionClick, isVisible, onHide }) => {
  const [isAnimating, setIsAnimating] = useState(false);
  const [shouldRender, setShouldRender] = useState(false);
  const [canScrollLeft, setCanScrollLeft] = useState(false);
  const [canScrollRight, setCanScrollRight] = useState(false);
  const containerRef = useRef(null);
  const scrollContainerRef = useRef(null);
  
  // Helper function to convert category to title case
  const formatCategory = (category) => {
    if (!category) return '';
    return category.charAt(0).toUpperCase() + category.slice(1).toLowerCase();
  };
  
  // Check scroll position to show/hide gradients
  const checkScrollPosition = () => {
    const container = scrollContainerRef.current;
    if (!container) return;
    
    const { scrollLeft, scrollWidth, clientWidth } = container;
    const threshold = 10; // Small threshold to handle rounding errors
    
    setCanScrollLeft(scrollLeft > threshold);
    setCanScrollRight(scrollLeft < scrollWidth - clientWidth - threshold);
  };
  
  // Handle appearing animation - only animate on initial show
  useEffect(() => {
    if (isVisible && suggestions.length > 0 && !shouldRender) {
      setShouldRender(true);
      setIsAnimating(true);
      // Small delay to ensure DOM is ready for animation
      setTimeout(() => {
        setIsAnimating(false);
      }, 50);
    }
  }, [isVisible, suggestions.length, shouldRender]);
  
  // Handle disappearing animation - only when explicitly hidden
  useEffect(() => {
    if (!isVisible && shouldRender) {
      setIsAnimating(true);
      // Wait for animation to complete before hiding
      setTimeout(() => {
        setShouldRender(false);
        setIsAnimating(false);
        onHide();
      }, 300);
    }
  }, [isVisible, shouldRender, onHide]);
  
  // Set up scroll event listener and check initial position
  useEffect(() => {
    const container = scrollContainerRef.current;
    if (!container) return;
    
    // Check initial scroll position
    checkScrollPosition();
    
    // Add scroll event listener
    container.addEventListener('scroll', checkScrollPosition);
    
    // Cleanup
    return () => {
      container.removeEventListener('scroll', checkScrollPosition);
    };
  }, [shouldRender]);
  
  // Check scroll position when suggestions change
  useEffect(() => {
    if (shouldRender && suggestions.length > 0) {
      // Small delay to ensure DOM is updated
      setTimeout(checkScrollPosition, 100);
    }
  }, [suggestions, shouldRender]);

  // Don't render if no suggestions
  if (!suggestions || suggestions.length === 0) {
    return null;
  }

  // Don't render if not supposed to render
  if (!shouldRender) {
    return null;
  }

  const handleSuggestionClick = (suggestion) => {
    // Trigger disappearing animation immediately
    setIsAnimating(true);
    onSuggestionClick(suggestion);
  };

  return (
    <div 
      ref={containerRef}
      className="transition-all duration-300 ease-out overflow-hidden"
      style={{
        height: isVisible && !isAnimating ? '110px' : '0px',
        marginBottom: isVisible && !isAnimating ? '8px' : '0px',
        opacity: isVisible && !isAnimating ? 1 : 0,
        transform: isVisible && !isAnimating ? 'translateY(0)' : 'translateY(-10px)',
        pointerEvents: isAnimating ? 'none' : 'auto'
      }}
    >
      <div className="max-w-[800px] mx-auto h-full max-[1200px]:max-w-[760px] max-[900px]:max-w-[700px] [@media(max-height:800px)]:max-w-[740px] [@media(max-height:700px)]:max-w-[680px]">
        {/* Clean, minimal suggestions container */}
        <div className="relative h-full flex items-center">
          <div 
            ref={scrollContainerRef}
            className="flex gap-3 overflow-x-auto scrollbar-hide"
            style={{
              scrollbarWidth: 'none',
              msOverflowStyle: 'none',
              WebkitOverflowScrolling: 'touch'
            }}
          >
            {suggestions.map((suggestion, index) => (
              <div
                key={index}
                className="flex-shrink-0 w-52"
                style={{
                  transform: isVisible && !isAnimating ? 'translateY(0)' : 'translateY(20px)',
                  opacity: isVisible && !isAnimating ? 1 : 0,
                  transition: `all 300ms ease-out ${index * 80}ms`
                }}
              >
                <button
                  onClick={() => handleSuggestionClick(suggestion)}
                  className="w-full h-full px-4 py-3 text-left rounded-[21px] border border-gray-300 dark:border-[#282828] bg-[#f2f2f2] dark:bg-[#181818] hover:bg-[#ebebeb] dark:hover:bg-[#202020] transition-colors duration-200 min-h-[80px] flex flex-col justify-center"
                >
                  <div className="flex items-center justify-between">
                    <div className="flex-1 min-w-0">
                      <h3 className="text-sm font-medium text-black dark:text-white mb-1">
                        {suggestion.title}
                      </h3>
                      <p className="text-xs text-[#9ca3af] dark:text-[#999999]">
                        {formatCategory(suggestion.category)}
                      </p>
                    </div>
                  </div>
                </button>
              </div>
            ))}
          </div>
          
          {/* Gradient fades to indicate scroll direction */}
          {canScrollLeft && (
            <div className="absolute left-0 top-0 bottom-0 w-24 bg-gradient-to-r from-[#ffffff] dark:from-[#111111] to-transparent pointer-events-none z-10" />
          )}
          {canScrollRight && (
            <div className="absolute right-0 top-0 bottom-0 w-24 bg-gradient-to-l from-[#ffffff] dark:from-[#111111] to-transparent pointer-events-none z-10" />
          )}
        </div>
      </div>
    </div>
  );
};

export default ChatSuggestions; 